import { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  /** Optional custom fallback UI */
  fallback?: ReactNode;
  /** Page name shown in the default fallback */
  pageName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log error + componentStack for dev console visibility
    console.error(
      '[ErrorBoundary] Uncaught error:',
      error,
      '\nComponent stack:',
      info.componentStack,
    );
  }

  /**
   * Reset boundary state so children re-mount without a full page reload.
   * This works because React will re-render the children tree from scratch
   * once hasError flips back to false.
   */
  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4 p-6 text-center">
          <AlertTriangle className="h-12 w-12 text-destructive" />
          <h2 className="text-xl font-semibold text-foreground">
            Noe gikk galt{this.props.pageName ? ` på ${this.props.pageName}` : ''}
          </h2>
          <p className="text-muted-foreground max-w-md text-sm">
            En uventet feil oppstod. Prøv å laste innholdet på nytt.
          </p>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <pre className="text-xs text-left bg-muted p-3 rounded max-w-lg overflow-auto max-h-32">
              {this.state.error.message}
            </pre>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={this.handleRetry}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Prøv igjen
            </Button>
            <Button variant="default" onClick={() => window.location.reload()}>
              Last siden på nytt
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
